import React from 'react';
import { tokens } from '../../../theme';
import { Box, Typography, useTheme, CircularProgress, Divider, Paper } from "@mui/material";
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import Header from '../../../components/Header';
import Axios from 'axios';
import Loading from '../../../components/Loading';
import Alert from '../../../components/alert/Alert';
import { useSignOut } from "react-auth-kit";
import { useNavigate } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import './Viewer.css';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import getCookies from '../../../utils/getCookies';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getAuthHeader } from '../../../utils/authToken';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import html2pdf from 'html2pdf.js/dist/html2pdf.bundle.min.js';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const StudentResult = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const testId = window.location.pathname.split("/")[window.location.pathname.split("/").length - 4];
    const courseId = window.location.pathname.split("/")[2];
    const studentUuid = window.location.pathname.split("/")[window.location.pathname.split("/").length - 2];
    
    // for 401 error
    const signOut = useSignOut();
    const navigate = useNavigate();
    const handleLogout = () => {
        signOut();
        localStorage.removeItem("userName");
        localStorage.removeItem("who");
        navigate("/signin");
    }
    const [loading, setLoading] = React.useState(true);

    const [pdfLoading, setPdfLoading] = React.useState(true);
    const [explanationPdfLoading, setExplanationPdfLoading] = React.useState(true);
    const [test, setTest] = React.useState(null);
    const [numPages, setNumPages] = React.useState(null);
    const [explanationNumPages, setExplanationNumPages] = React.useState(null);
    const [sections, setSections] = React.useState([]);

    const [testTakerInfo, setTestTakerInfo] = React.useState(null);

    // const [unansweredQuestions, setUnansweredQuestions] = React.useState([]);

    const [questionsForNoSectionTest, setQuestionsForNoSectionTest] = React.useState([]);
    const [studentFrqAnswers, setStudentFrqAnswers] = React.useState([]);

    // for alert
    const [successAlertOpen, setSuccessAlertOpen] = React.useState(false);
    const [errorAlertOpen, setErrorAlertOpen] = React.useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = React.useState("");
    const [errorAlertMessage, setErrorAlertMessage] = React.useState("");

    // FRQ editing state: { [frqAnswerId]: { score, feedback_text } }
    const [editingFrq, setEditingFrq] = React.useState({});
    const [exporting, setExporting] = React.useState(false);

    const canEditFrq = userRole === 'admin' || userRole === 'teacher';

    const startEditingFrq = (frqAnswer) => {
        const feedbackJson = frqAnswer.feedback_json
            ? (typeof frqAnswer.feedback_json === 'string' ? JSON.parse(frqAnswer.feedback_json) : frqAnswer.feedback_json)
            : null;
        // For SAQ: simple feedback text. For rubric-style: overall_feedback
        const feedbackText = feedbackJson?.categories
            ? (feedbackJson.overall_feedback || '')
            : (feedbackJson?.feedback || feedbackJson?.reasoning || frqAnswer.feedback_text || '');
        setEditingFrq(prev => ({
            ...prev,
            [frqAnswer.id]: { score: frqAnswer.score ?? 0, feedback_text: feedbackText }
        }));
    };

    const cancelEditingFrq = (frqAnswerId) => {
        setEditingFrq(prev => {
            const next = { ...prev };
            delete next[frqAnswerId];
            return next;
        });
    };

    const saveEditedFrq = async (frqAnswer) => {
        const edit = editingFrq[frqAnswer.id];
        if (!edit) return;
        try {
            // Build updated feedback_json
            let updatedFeedbackJson = frqAnswer.feedback_json
                ? (typeof frqAnswer.feedback_json === 'string' ? JSON.parse(frqAnswer.feedback_json) : { ...frqAnswer.feedback_json })
                : {};

            if (updatedFeedbackJson.categories) {
                updatedFeedbackJson.overall_feedback = edit.feedback_text;
            } else {
                updatedFeedbackJson.feedback = edit.feedback_text;
            }

            await Axios.patch(
                `${process.env.REACT_APP_URL}/api/v1/student-frq-answers/${frqAnswer.id}`,
                { score: parseInt(edit.score), feedback_json: updatedFeedbackJson, feedback_text: edit.feedback_text },
                { headers: { Authorization: getAuthHeader(), 'Content-Type': 'application/json' } }
            );

            // Update local state
            setStudentFrqAnswers(prev => prev.map(a =>
                a.id === frqAnswer.id
                    ? { ...a, score: parseInt(edit.score), feedback_json: updatedFeedbackJson, feedback_text: edit.feedback_text }
                    : a
            ));
            cancelEditingFrq(frqAnswer.id);
            setSuccessAlertMessage("FRQ answer updated");
            setSuccessAlertOpen(true);
        } catch (err) {
            setErrorAlertMessage(err.response?.data?.error || "Failed to update");
            setErrorAlertOpen(true);
        }
    };


    const [dividerPosition, setDividerPosition] = React.useState(7); // Initial flex ratio
    const rightPaneRef = React.useRef(null);

    // Toggle between test PDF and explanation PDF
    const [viewMode, setViewMode] = React.useState('test'); // 'test' or 'explanation'

    const handleMouseDown = (e) => {
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (rightPaneRef.current) {
            const newDividerPosition = (e.clientX / window.innerWidth) * 10;
            setDividerPosition(newDividerPosition);
        }
    };

    const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    // Handle view mode toggle between test and explanation PDF
    const handleViewModeToggle = () => {
        setViewMode(viewMode === 'test' ? 'explanation' : 'test');
    };

    const parseStudentAnswers = (studentAnswers, hasSection) => {
        if (hasSection) {
            return studentAnswers.split('@').map(section => section.split('|'));
        } else {
            return studentAnswers.split('|');
        }
    };
    
    let parsedStudentAnswers = [];
    if (testTakerInfo && testTakerInfo.student_answers && test) {
        parsedStudentAnswers = parseStudentAnswers(testTakerInfo.student_answers, test.has_section);
    }
    
    React.useEffect(() => {
        localStorage.setItem('selectedMenu', 'Tests');
    
        async function fetchTest() {
            await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/test-taking/${testId}`, {
                headers: {
                    Authorization: getAuthHeader(),
                },
            }).then(async (response) => {
                setTest(response.data);
                if (response.data.test_sections) {
                    setSections(response.data.test_sections.map(section => ({
                        ...section,
                        test_questions: section.test_questions.map(question => ({
                            ...question,
                            student_pick: ''
                        }))
                    })));
                } else {
                    setQuestionsForNoSectionTest(response.data.test_questions.map(question => ({
                        ...question,
                        student_pick: ''
                    })));
                }
            }).catch((error) => {
                if (error.response?.status === 404) {
                    setLoading(false);
                    setErrorAlertMessage('Test not found!');
                    setErrorAlertOpen(true);
                    return;
                } else if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(error.response?.data?.message || "Something went wrong");
                setErrorAlertOpen(true);
            });
        }
        fetchTest();
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    

    React.useEffect(() => {
        const fetchStudentTestInfo = async () => {
            try {
                const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/courses/${courseId}/test-taking/${testId}/students/${studentUuid}/student-answers`, {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                });
    
                if (response && response.data) {
                    setTestTakerInfo(response.data);
                    if (response.data.student_frq_answers) {
                        setStudentFrqAnswers(response.data.student_frq_answers);
                    }
                    if (response.data.student_answers) {
                        const parsedAnswers = parseStudentAnswers(response.data.student_answers, test.has_section);
                        if (test.has_section) {
                            setSections(prevSections => prevSections.map((section, sIndex) => ({
                                ...section,
                                test_questions: section.test_questions.map((question, qIndex) => ({
                                    ...question,
                                    student_pick: parsedAnswers[sIndex] && parsedAnswers[sIndex][qIndex] ? parsedAnswers[sIndex][qIndex] : ''
                                }))
                            })));
                        } else {
                            setQuestionsForNoSectionTest(prevQuestions => prevQuestions.map((question, qIndex) => ({
                                ...question,
                                student_pick: parsedAnswers[qIndex] ? parsedAnswers[qIndex] : ''
                            })));
                        }
                    }
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 401) {
                        handleLogout();
                    } else if (error.response.status === 404) {
                        setTestTakerInfo(null);
                        console.log("Test not found");
                    }
                } else {
                    console.log("An unexpected error occurred:", error.message);
                }
            }
        };
    
        fetchStudentTestInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [test, courseId, testId, studentUuid]);
            

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
        setPdfLoading(false);
    };

    const onExplanationDocumentLoadSuccess = ({ numPages }) => {
        setExplanationNumPages(numPages);
        setExplanationPdfLoading(false);
    };

    // Memoize pdf.js options to prevent re-triggering Document load on every render.
    // disableAutoFetch stops pdf.js from pre-downloading all pages after the first loads.
    const pdfOptions = React.useMemo(() => ({
        disableAutoFetch: true,
        disableStream: false,
    }), []);

    // Render both PDFs - toggle visibility instead of re-mounting
    const pixelRatio = Math.max(window.devicePixelRatio || 1, 2);

    const testPdfRender = () => {
        const pdfUrl = (userRole === "teacher" && test?.teacher_file_url) ? test.teacher_file_url : test?.file_url;
        return (
            <Document
                file={pdfUrl}
                options={pdfOptions}
                onLoadSuccess={onDocumentLoadSuccess}
                onContextMenu={(e) => e.preventDefault()}
                className="pdf-container"
                loading={
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress color="inherit" size={40} />
                    </Box>
                }
            >
                {Array.from(new Array(numPages), (el, index) => (
                    <Page
                        key={`test_page_${index + 1}`}
                        pageNumber={index + 1}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        scale={pixelRatio}
                        loading={
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress color="inherit" size={40} />
                            </Box>
                        }
                    />
                ))}
            </Document>
        );
    };

    const explanationPdfRender = () => {
        if (!test?.explanations_file_url) return null;
        return (
            <Document
                file={test?.explanations_file_url}
                options={pdfOptions}
                onLoadSuccess={onExplanationDocumentLoadSuccess}
                onContextMenu={(e) => e.preventDefault()}
                className="pdf-container"
                loading={
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress color="inherit" size={40} />
                    </Box>
                }
            >
                {Array.from(new Array(explanationNumPages), (el, index) => (
                    <Page
                        key={`explanation_page_${index + 1}`}
                        pageNumber={index + 1}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        scale={pixelRatio}
                        loading={
                            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                                <CircularProgress color="inherit" size={40} />
                            </Box>
                        }
                    />
                ))}
            </Document>
        );
    };

    const compareAnswers = (correctAnswer, studentPick, isMultiSelect) => {
        if (isMultiSelect) {
            // For multi-select, compare sorted comma-separated values
            const correctSet = (correctAnswer || '').split(',').filter(a => a).sort().join(',');
            const studentSet = (studentPick || '').split(',').filter(a => a).sort().join(',');
            return correctSet === studentSet;
        }
        return correctAnswer === studentPick;
    };

    const calculateSectionScore = (sectionIndex) => {
        let score = 0;
        if (!parsedStudentAnswers[sectionIndex]) return score;

        sections[sectionIndex].test_questions.forEach((question, qIndex) => {
            if (compareAnswers(question.answer, parsedStudentAnswers[sectionIndex][qIndex], question.is_multi_select)) {
                score++;
            }
        });
        return score;
    };

    // --- FRQ helpers ---

    const getFrqAnswerForQuestion = (questionId) => {
        return studentFrqAnswers.filter(a => a.question_id === questionId);
    };

    const computeFrqTotals = () => {
        let score = 0;
        let maxScore = 0;
        // Use admin override if set
        if (testTakerInfo?.frq_score_override != null) {
            score = testTakerInfo.frq_score_override;
        } else {
            // Score from submitted answers
            studentFrqAnswers.forEach(a => {
                if (a.score != null) score += a.score;
            });
        }
        // Max from question definitions (not answer rows — unanswered questions still count toward max)
        const allQuestions = test.has_section
            ? sections.flatMap(s => s.test_questions)
            : questionsForNoSectionTest;
        // For choice groups, only count the highest max_points once per group
        const choiceGroupMaxes = {};
        allQuestions.forEach(q => {
            if (q.question_type !== 'frq' && q.question_type !== 'sa') return;
            if (q.choice_group) {
                choiceGroupMaxes[q.choice_group] = Math.max(choiceGroupMaxes[q.choice_group] || 0, q.max_points || 0);
            } else {
                maxScore += (q.max_points || 0);
            }
        });
        Object.values(choiceGroupMaxes).forEach(v => { maxScore += v; });
        return { score, maxScore };
    };

    // Compute FRQ score for a specific section
    const computeSectionFrqScore = (sectionIndex) => {
        const section = test.has_section ? sections[sectionIndex] : null;
        if (!section) return { score: 0, maxScore: 0 };
        let score = 0;
        let maxScore = 0;
        section.test_questions.forEach(q => {
            if (q.question_type !== 'frq' && q.question_type !== 'sa') return;
            maxScore += (q.max_points || 0);
            const answers = studentFrqAnswers.filter(a => a.question_id === q.id);
            answers.forEach(a => {
                if (a.score != null) score += a.score;
            });
        });
        return { score, maxScore };
    };

    // Count MCQ questions in a section
    const countSectionMcq = (sectionIndex) => {
        const section = test.has_section ? sections[sectionIndex] : null;
        if (!section) return 0;
        return section.test_questions.filter(q => q.question_type !== 'frq' && q.question_type !== 'sa').length;
    };

    const computeMcqTotal = () => {
        if (!testTakerInfo || !test) return { score: 0, total: 0 };
        if (test.has_section) {
            let score = 0;
            let total = 0;
            sections.forEach((section, sIndex) => {
                section.test_questions.forEach((q, qIdx) => {
                    if (q.question_type === 'frq' || q.question_type === 'sa') return;
                    total++;
                    if (compareAnswers(q.answer, parsedStudentAnswers[sIndex]?.[qIdx], q.is_multi_select)) {
                        score++;
                    }
                });
            });
            return { score, total };
        } else {
            let score = 0;
            let total = 0;
            questionsForNoSectionTest.forEach((q, qIdx) => {
                if (q.question_type === 'frq' || q.question_type === 'sa') return;
                total++;
                if (compareAnswers(q.answer, parsedStudentAnswers[qIdx], q.is_multi_select)) {
                    score++;
                }
            });
            return { score, total };
        }
    };

    const hasFrqQuestions = () => {
        if (!test) return false;
        // Also true when admin has set an FRQ score override on MCQ-only tests
        if (testTakerInfo?.frq_score_override != null) return true;
        const allQuestions = test.has_section
            ? sections.flatMap(s => s.test_questions)
            : questionsForNoSectionTest;
        return allQuestions.some(q => q.question_type === 'frq' || q.question_type === 'sa');
    };

    // Detect which non-MCQ types are in the test for score labels
    const getTestQuestionTypes = () => {
        if (!test) return { hasMcq: false, hasFrq: false, hasSa: false };
        const allQuestions = test.has_section
            ? sections.flatMap(s => s.test_questions)
            : questionsForNoSectionTest;
        return {
            hasMcq: allQuestions.some(q => q.question_type !== 'frq' && q.question_type !== 'sa'),
            hasFrq: allQuestions.some(q => q.question_type === 'frq'),
            hasSa: allQuestions.some(q => q.question_type === 'sa'),
        };
    };

    // Get the label for the non-MCQ score bucket
    const getNonMcqLabel = () => {
        const types = getTestQuestionTypes();
        if (types.hasFrq && types.hasSa) return 'FRQ + SA';
        if (types.hasSa) return 'SA';
        return 'FRQ';
    };

    // Check if any FRQ answer is still waiting to be graded
    const isFrqGradingPending = () => {
        if (!studentFrqAnswers.length) return false;
        return studentFrqAnswers.some(a => a.grading_status === 'pending' || a.grading_status === 'grading');
    };

    const canExportResults = () => {
        if (!test || !testTakerInfo?.is_completed) return false;
        if (userRole === 'teacher') return false;
        if (userRole === 'student' && !test.release_grades) return false;
        if (hasFrqQuestions() && isFrqGradingPending()) return false;
        return true;
    };

    const fetchImageAsBase64 = (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvas.getContext('2d').drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(null);
            // Add cache-bust to force CORS preflight
            img.src = url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now();
        });
    };

    const handleExportPdf = async () => {
        if (exporting) return;
        setExporting(true);
        try {
        const mcq = computeMcqTotal();
        const frq = computeFrqTotals();
        const types = getTestQuestionTypes();
        const nonMcqLabel = getNonMcqLabel();
        const allQuestions = test.has_section
            ? sections.flatMap(s => s.test_questions)
            : questionsForNoSectionTest;
        const studentName = testTakerInfo?.student_name || 'Student';
        const testTitle = test?.title || 'Test';

        // Build MCQ rows
        let mcqHtml = '';
        if (types.hasMcq) {
            let mcqRows = '';
            if (test.has_section) {
                sections.forEach((section, sIdx) => {
                    section.test_questions.forEach((q, qIdx) => {
                        if (q.question_type === 'frq' || q.question_type === 'sa') return;
                        const studentAnswer = parsedStudentAnswers[sIdx]?.[qIdx] || '—';
                        const correct = compareAnswers(q.answer, studentAnswer, q.is_multi_select);
                        mcqRows += `<tr>
                            <td>Q${q.question_number}</td>
                            <td>${studentAnswer}</td>
                            <td>${q.answer}</td>
                            <td style="color:${correct ? '#2e7d32' : '#c62828'};font-weight:bold">${correct ? '✓' : '✗'}</td>
                        </tr>`;
                    });
                });
            } else {
                questionsForNoSectionTest.forEach((q, qIdx) => {
                    if (q.question_type === 'frq' || q.question_type === 'sa') return;
                    const studentAnswer = parsedStudentAnswers[qIdx] || '—';
                    const correct = compareAnswers(q.answer, studentAnswer, q.is_multi_select);
                    mcqRows += `<tr>
                        <td>Q${q.question_number}</td>
                        <td>${studentAnswer}</td>
                        <td>${q.answer}</td>
                        <td style="color:${correct ? '#2e7d32' : '#c62828'};font-weight:bold">${correct ? '✓' : '✗'}</td>
                    </tr>`;
                });
            }
            mcqHtml = `
                <h3>Multiple Choice — ${mcq.score} / ${mcq.total}</h3>
                <table>
                    <thead><tr><th>Q#</th><th>Your Answer</th><th>Correct Answer</th><th>Result</th></tr></thead>
                    <tbody>${mcqRows}</tbody>
                </table>`;
        }

        // Pre-fetch all FRQ images as base64 to avoid CORS issues
        const imageCache = {};
        const allImageUrls = studentFrqAnswers
            .flatMap(a => a.response_images || [])
            .filter(Boolean);
        if (allImageUrls.length > 0) {
            const results = await Promise.all(allImageUrls.map(url => fetchImageAsBase64(url)));
            allImageUrls.forEach((url, i) => { if (results[i]) imageCache[url] = results[i]; });
        }

        // Build FRQ/SA section
        let frqHtml = '';
        if (types.hasFrq || types.hasSa) {
            let frqContent = '';
            const frqQuestions = allQuestions.filter(q => q.question_type === 'frq' || q.question_type === 'sa');
            frqQuestions.forEach(q => {
                const answers = getFrqAnswerForQuestion(q.id)
                    .sort((a, b) => (a.part_label || '').localeCompare(b.part_label || ''));
                if (!answers.length) return;

                frqContent += `<div class="frq-question"><h4>Q${q.question_number} <span class="frq-type">(${
                    q.question_type === 'sa' ? 'Short Answer' : q.frq_type === 'dbq' ? 'DBQ' : q.frq_type === 'leq' ? 'LEQ' : 'FRQ'
                })</span></h4>`;

                answers.forEach(a => {
                    const feedbackJson = a.feedback_json
                        ? (typeof a.feedback_json === 'string' ? JSON.parse(a.feedback_json) : a.feedback_json)
                        : null;

                    frqContent += `<div class="frq-part">`;
                    if (a.part_label) frqContent += `<strong>Part ${a.part_label}</strong>`;
                    if (a.score != null) frqContent += ` — Score: ${a.score} / ${a.max_score}`;
                    frqContent += `<br/>`;

                    if (a.response_text) {
                        frqContent += `<div class="response">${a.response_text}</div>`;
                    }
                    if (a.response_images?.length) {
                        frqContent += `<div class="response" style="display:flex;flex-wrap:wrap;gap:8px;">`;
                        a.response_images.forEach((url, imgIdx) => {
                            const src = imageCache[url] || url;
                            frqContent += `<img src="${src}" alt="Upload ${imgIdx + 1}" style="max-width:200px;max-height:200px;border:1px solid #ddd;border-radius:4px;" />`;
                        });
                        frqContent += `</div>`;
                    }

                    // Rubric-style feedback (categories table)
                    if (feedbackJson?.categories) {
                        frqContent += `<table class="rubric"><thead><tr><th>Category</th><th>Score</th><th>Max</th><th>Reasoning</th></tr></thead><tbody>`;
                        feedbackJson.categories.forEach(cat => {
                            frqContent += `<tr><td>${cat.name}</td><td>${cat.score}</td><td>${cat.max_score}</td><td>${cat.reasoning}</td></tr>`;
                        });
                        frqContent += `</tbody></table>`;
                        if (feedbackJson.overall_feedback) {
                            frqContent += `<div class="feedback">${feedbackJson.overall_feedback}</div>`;
                        }
                    } else if (feedbackJson) {
                        // Simple SAQ feedback
                        if (feedbackJson.reasoning) frqContent += `<div class="reasoning"><strong>Reasoning:</strong> ${feedbackJson.reasoning}</div>`;
                        if (feedbackJson.feedback) frqContent += `<div class="feedback">${feedbackJson.feedback}</div>`;
                    }
                    frqContent += `</div>`;
                });
                frqContent += `</div>`;
            });

            frqHtml = `<h3>${nonMcqLabel} — ${frq.score} / ${frq.maxScore}</h3>${frqContent}`;
        }

        // Total + AP score
        const totalScore = mcq.score + frq.score;
        const totalMax = mcq.total + frq.maxScore;
        let summaryHtml = `<div class="summary"><h3>Total: ${totalScore} / ${totalMax}</h3>`;
        if (testTakerInfo.ap_score != null) {
            summaryHtml += `<h3>Estimated AP Score: ${testTakerInfo.ap_score}</h3>`;
        }
        summaryHtml += `</div>`;

        const html = `
            <div id="export-content" style="font-family: 'Helvetica Neue', Arial, sans-serif; color: #222; padding: 20px; max-width: 800px;">
                <style>
                    h2 { margin: 0 0 4px; font-size: 20px; }
                    h3 { margin: 16px 0 8px; font-size: 16px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
                    h4 { margin: 12px 0 4px; font-size: 14px; }
                    table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
                    th, td { border: 1px solid #ddd; padding: 4px 8px; text-align: left; }
                    th { background: #f5f5f5; font-weight: 600; }
                    .subtitle { color: #666; font-size: 13px; margin-bottom: 12px; }
                    .summary h3 { color: #1a5; border: none; }
                    .frq-question { margin-bottom: 12px; }
                    .frq-type { color: #666; font-weight: normal; font-size: 12px; }
                    .frq-part { margin: 6px 0 10px 8px; padding-left: 8px; border-left: 2px solid #ddd; font-size: 13px; }
                    .response { background: #f9f9f9; padding: 6px 8px; border-radius: 4px; margin: 4px 0; font-size: 12px; }
                    .reasoning, .feedback { font-size: 12px; margin: 4px 0; color: #555; }
                    .feedback { font-style: italic; }
                    table.rubric { font-size: 11px; }
                </style>
                <h2>${testTitle}</h2>
                <div class="subtitle">${studentName} — ${new Date().toLocaleDateString()}</div>
                ${summaryHtml}
                ${mcqHtml}
                ${frqHtml}
            </div>`;

        const container = document.createElement('div');
        container.style.cssText = 'position:absolute;left:0;top:0;width:800px;z-index:-1;opacity:0;pointer-events:none;';
        container.innerHTML = html;
        document.body.appendChild(container);

        // Let images load before capturing
        await new Promise(r => setTimeout(r, 200));

        await html2pdf().set({
            margin: [10, 10, 10, 10],
            filename: `${testTitle.replace(/[^a-zA-Z0-9]/g, '_')}_Results.pdf`,
            image: { type: 'jpeg', quality: 0.95 },
            html2canvas: { scale: 2, useCORS: true, windowWidth: 800 },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
            pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        }).from(container.firstElementChild).save();

        document.body.removeChild(container);
        } catch (err) {
            console.error('PDF export error:', err);
        } finally {
            setExporting(false);
        }
    };

    const renderFrqRubricTable = (feedbackJson) => {
        if (!feedbackJson || !feedbackJson.categories) return null;
        return (
            <Box sx={{ mt: 1, mb: 2 }}>
                <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Score</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Max Score</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Reasoning</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {feedbackJson.categories.map((cat, i) => (
                                <TableRow key={i}>
                                    <TableCell>{cat.name}</TableCell>
                                    <TableCell align="center">{cat.score}</TableCell>
                                    <TableCell align="center">{cat.max_score}</TableCell>
                                    <TableCell>{cat.reasoning}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{feedbackJson.total_score}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{feedbackJson.max_score}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                {feedbackJson.overall_feedback && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                        {feedbackJson.overall_feedback}
                    </Typography>
                )}
            </Box>
        );
    };

    const renderSaAnswer = (question, showGrades = true) => {
        const frqAnswers = getFrqAnswerForQuestion(question.id);
        if (!frqAnswers.length) {
            return (
                <Box sx={{ mt: 1, mb: 2, p: 2, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1, border: `1px solid ${colors.grey[600]}` }}>
                    <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                        No response submitted.
                    </Typography>
                </Box>
            );
        }
        const saAnswer = frqAnswers[0];
        const isEditing = editingFrq[saAnswer.id];
        const feedbackJson = saAnswer.feedback_json
            ? (typeof saAnswer.feedback_json === 'string' ? JSON.parse(saAnswer.feedback_json) : saAnswer.feedback_json)
            : null;
        const isCorrect = feedbackJson?.is_correct;

        return (
            <Box sx={{ mt: 1, mb: 2, p: 2, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1, border: `1px solid ${!showGrades ? colors.grey[600] : isCorrect ? colors.greenAccent[500] : colors.redAccent ? colors.redAccent[500] : '#f44336'}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {showGrades && isCorrect !== undefined && (
                            isCorrect
                                ? <CheckCircleIcon sx={{ color: 'green', fontSize: 20 }} />
                                : <CancelIcon sx={{ color: 'red', fontSize: 20 }} />
                        )}
                        <Typography variant="body2">
                            <strong>Your Answer:</strong> {saAnswer.response_text || '(no response)'}
                        </Typography>
                    </Box>
                    {showGrades && canEditFrq && !isEditing && (
                        <IconButton size="small" onClick={() => startEditingFrq(saAnswer)} sx={{ color: colors.grey[300] }}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    )}
                    {isEditing && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => saveEditedFrq(saAnswer)} sx={{ color: colors.greenAccent[400] }}>
                                <SaveIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => cancelEditingFrq(saAnswer.id)} sx={{ color: colors.redAccent ? colors.redAccent[400] : '#f44336' }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
                {showGrades && !isEditing && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: colors.greenAccent[400] }}>
                        Correct Answer: {question.accepted_answers?.length > 0 ? question.accepted_answers.join(', ') : (feedbackJson?.expected || '')}
                    </Typography>
                )}
                {showGrades && isEditing && (
                    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <TextField
                            size="small"
                            label="Score (max 1)"
                            type="number"
                            value={isEditing.score}
                            onChange={(e) => setEditingFrq(prev => ({
                                ...prev,
                                [saAnswer.id]: { ...prev[saAnswer.id], score: e.target.value }
                            }))}
                            inputProps={{ min: 0, max: 1 }}
                            sx={{ width: 150 }}
                        />
                    </Box>
                )}
                {showGrades && saAnswer.score != null && !isEditing && (
                    <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 'bold' }}>
                        Score: {saAnswer.score} / {saAnswer.max_score}
                    </Typography>
                )}
            </Box>
        );
    };

    const renderFrqAnswer = (question, showGrades = true) => {
        // Use SA-specific renderer for SA questions
        if (question.question_type === 'sa') {
            return renderSaAnswer(question, showGrades);
        }

        const frqAnswers = getFrqAnswerForQuestion(question.id);
        if (!frqAnswers.length) {
            return (
                <Box sx={{ mt: 1, mb: 2, p: 2, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1, border: `1px solid ${colors.grey[600]}` }}>
                    <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                        No FRQ response submitted.
                    </Typography>
                </Box>
            );
        }

        // For students: don't show any scores until ALL FRQ grading is complete
        const anyFrqStillGrading = userRole === 'student' && isFrqGradingPending();
        const effectiveShowGrades = showGrades && !anyFrqStillGrading;

        return frqAnswers.sort((a, b) => (a.part_label || '').localeCompare(b.part_label || '')).map((frqAnswer, idx) => {
            const isGrading = anyFrqStillGrading || frqAnswer.grading_status === 'pending' || frqAnswer.grading_status === 'grading';
            const isManual = frqAnswer.grading_status === 'manual';
            let feedbackJson = null;
            if (effectiveShowGrades && frqAnswer.feedback_json) {
                feedbackJson = typeof frqAnswer.feedback_json === 'string'
                    ? JSON.parse(frqAnswer.feedback_json)
                    : frqAnswer.feedback_json;
            }
            const isRubricStyle = feedbackJson && feedbackJson.categories;
            const isEditing = editingFrq[frqAnswer.id];

            return (
                <Box key={frqAnswer.id || idx} sx={{ mt: 1, mb: 2, p: 2, backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: 1, border: `1px solid ${colors.grey[600]}` }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {frqAnswer.part_label && (
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Part {frqAnswer.part_label}
                            </Typography>
                        )}
                        {effectiveShowGrades && canEditFrq && !isGrading && !isEditing && (
                            <IconButton size="small" onClick={() => startEditingFrq(frqAnswer)} sx={{ color: colors.grey[300] }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                        {isEditing && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton size="small" onClick={() => saveEditedFrq(frqAnswer)} sx={{ color: colors.greenAccent[400] }}>
                                    <SaveIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={() => cancelEditingFrq(frqAnswer.id)} sx={{ color: colors.redAccent[400] }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                    {frqAnswer.response_text && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Response:</strong> {frqAnswer.response_text}
                        </Typography>
                    )}
                    {frqAnswer.response_images && frqAnswer.response_images.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Uploaded Images:</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {frqAnswer.response_images.map((url, imgIdx) => (
                                    <Box key={imgIdx} sx={{ width: 150, height: 150, borderRadius: 1, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer' }}
                                        onClick={() => window.open(url, '_blank')}>
                                        <img src={url} alt={`Upload ${imgIdx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                    {!frqAnswer.response_text && (!frqAnswer.response_images || frqAnswer.response_images.length === 0) && (
                        <Typography variant="body2" sx={{ mb: 1 }}><strong>Response:</strong> (no response)</Typography>
                    )}

                    {!effectiveShowGrades ? null : isGrading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2">Grading in progress...</Typography>
                        </Box>
                    ) : isManual && !isEditing ? (
                        <Typography variant="body2" sx={{ mt: 1, color: colors.greenAccent[400], fontStyle: 'italic' }}>
                            Awaiting manual grading (no rubric attached)
                        </Typography>
                    ) : isEditing ? (
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <TextField
                                size="small"
                                label={`Score (max ${frqAnswer.max_score})`}
                                type="number"
                                value={isEditing.score}
                                onChange={(e) => setEditingFrq(prev => ({
                                    ...prev,
                                    [frqAnswer.id]: { ...prev[frqAnswer.id], score: e.target.value }
                                }))}
                                inputProps={{ min: 0, max: frqAnswer.max_score }}
                                sx={{ width: 150 }}
                            />
                            <TextField
                                size="small"
                                label="Feedback"
                                multiline
                                minRows={2}
                                maxRows={6}
                                value={isEditing.feedback_text}
                                onChange={(e) => setEditingFrq(prev => ({
                                    ...prev,
                                    [frqAnswer.id]: { ...prev[frqAnswer.id], feedback_text: e.target.value }
                                }))}
                                fullWidth
                            />
                        </Box>
                    ) : (
                        <>
                            {frqAnswer.score != null && (
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Score: {frqAnswer.score} / {frqAnswer.max_score}
                                </Typography>
                            )}
                            {isRubricStyle ? (
                                renderFrqRubricTable(feedbackJson)
                            ) : feedbackJson ? (
                                <Box sx={{ mt: 1 }}>
                                    {feedbackJson.reasoning && (
                                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                                            <strong>Reasoning:</strong> {feedbackJson.reasoning}
                                        </Typography>
                                    )}
                                    {feedbackJson.feedback && (
                                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                            {feedbackJson.feedback}
                                        </Typography>
                                    )}
                                </Box>
                            ) : frqAnswer.feedback_text ? (
                                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                    {frqAnswer.feedback_text}
                                </Typography>
                            ) : null}
                        </>
                    )}
                </Box>
            );
        });
    };

    // --- End FRQ helpers ---

    return (
        <Box m="20px" marginX={3} marginY={3}>
            <Header title={test?.course_tests[0].title} subtitle="A ONE Institute" />
            {loading ? 
                <Loading />
            : <>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '20px',
                        marginBottom: '10px',
                        position: 'relative',
                        mt: 5,
                        overflow: 'visible',
                    }}
                >
                    {/* Open PDF - only visible to admins to prevent downloads */}
                    {userRole === "admin" && (
                        <a href={test?.file_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    position: 'absolute',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    left: '0',
                                    display: 'flex',
                                    gap: '10px',
                                    alignItems: 'center',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    backgroundColor: colors.greenAccent[500],
                                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                    color: colors.primary[400],
                                }}
                            >
                                Open Test file in PDF
                                <OpenInNewIcon fontSize='small' sx={{ cursor: 'pointer' }} />
                            </Typography>
                        </a>
                    )}
                { userRole !== "student" && 
                    <Box sx={{ position: 'absolute', top: '50%', right: '0', transform: 'translateY(20%)' }}>
                        <Link to={`/classrooms/${courseId}/tests/${testId}/grading`} style={{ textDecoration: 'none' }}>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: colors.blueAccent[600],
                                    color: 'white',
                                    padding: '10px 20px',
                                    borderRadius: '5px',
                                    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                    ':hover': {
                                        backgroundColor: colors.blueAccent[700],
                                    },
                                }}
                            >
                                See Results
                            </Button>
                        </Link>
                    </Box>
                }
                {/* Download Results PDF */}
                {canExportResults() && (
                    <Box sx={{
                        position: 'absolute',
                        top: '-50px',
                        right: userRole !== "student"
                            ? (test?.explanations_file_url ? '340px' : '150px')
                            : (test?.explanations_file_url ? '200px' : '0'),
                    }}>
                        <Button
                            variant="contained"
                            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                            onClick={handleExportPdf}
                            disabled={exporting}
                            sx={{
                                backgroundColor: colors.greenAccent[600],
                                color: 'white',
                                padding: '10px 20px',
                                borderRadius: '5px',
                                ':hover': { backgroundColor: colors.greenAccent[700] },
                            }}
                        >
                            {exporting ? 'Preparing PDF...' : 'Download Results'}
                        </Button>
                    </Box>
                )}
                {/* View Explanation Toggle - only show if:
                    1. Explanation file exists
                    2. Student has completed the test
                    3. Grades are released (for students) OR user is not a student */}
                {test?.explanations_file_url &&
                 testTakerInfo?.is_completed &&
                 (test.release_grades || userRole !== "student") && (
                    <Box sx={{
                        position: 'absolute',
                        top: '-50px',
                        right: userRole !== "student" ? '150px' : '0',
                    }}>
                        <Button
                            variant="contained"
                            onClick={handleViewModeToggle}
                            sx={{
                                backgroundColor: viewMode === 'test' ? colors.greenAccent[600] : colors.blueAccent[600],
                                color: 'white',
                                padding: '10px 20px',
                                borderRadius: '5px',
                                boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.2)',
                                ':hover': {
                                    backgroundColor: viewMode === 'test' ? colors.greenAccent[700] : colors.blueAccent[700],
                                },
                            }}
                        >
                            {viewMode === 'test' ? 'View Explanation' : 'View Test'}
                        </Button>
                    </Box>
                )}
                </Box>
                <Box
                    display="flex"
                    width="100%"
                    bgcolor={colors.primary[600]}
                >
                    {/* Test PDF Container - always rendered, visibility toggled */}
                    <Box
                        flex={dividerPosition}
                        width="100%"
                        sx={{
                            overflowY: 'scroll',
                            height: '80vh',
                            left: 0,
                            display: viewMode === 'test' ? 'block' : 'none'
                        }}
                    >
                        <Box sx={{ position: "sticky", top: 0 }}>
                            {testPdfRender()}
                        </Box>
                    </Box>
                    {/* Explanation PDF Container - only rendered if explanation exists, visibility toggled */}
                    {test?.explanations_file_url && (
                        <Box
                            flex={dividerPosition}
                            width="100%"
                            sx={{
                                overflowY: 'scroll',
                                height: '80vh',
                                left: 0,
                                display: viewMode === 'explanation' ? 'block' : 'none'
                            }}
                        >
                            <Box sx={{ position: "sticky", top: 0 }}>
                                {explanationPdfRender()}
                            </Box>
                        </Box>
                    )}
                    <Divider 
                        orientation="vertical" 
                        flexItem
                        onMouseDown={handleMouseDown}
                        sx={{ cursor: 'col-resize', width: '5px', bgcolor: "grey" }}
                    />
                    <Box
                        flex={10 - dividerPosition}
                        ref={rightPaneRef}  
                        width="100%" 
                        p={5}
                        display='flex'
                        flexDirection='column'
                        gap={3}
                        sx={{
                            overflowY: 'auto',
                            overscrollBehavior: 'contain',
                            height: '80vh',
                        }}
                    > 
                    {/* Check if grades should be shown to students */}
                    {userRole === "student" && !test?.release_grades && testTakerInfo?.is_completed && (
                        <Box sx={{ mb: 3, p: 2, backgroundColor: colors.blueAccent[700], borderRadius: 1 }}>
                            <Typography variant="body1">
                                Your test has been submitted. Grades will be available once they are released.
                            </Typography>
                        </Box>
                    )}
                    {/* With Section */}
                        {test ? test.has_section ? (
                            <>
                                {testTakerInfo && testTakerInfo.is_completed === true && (test.release_grades || userRole !== "student") && (
                                    hasFrqQuestions() ? (
                                        <Box sx={{ mb: 2 }}>
                                            {(() => { const mcq = computeMcqTotal(); const frq = computeFrqTotals(); const pending = isFrqGradingPending(); const hasAnyFrqAnswers = studentFrqAnswers.length > 0; const types = getTestQuestionTypes(); const nonMcqLabel = getNonMcqLabel(); return (<>
                                                {mcq.total > 0 && (
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                        MCQ Score: {mcq.score} / {mcq.total}
                                                    </Typography>
                                                )}
                                                {(!hasAnyFrqAnswers || pending) && types.hasFrq ? (
                                                    <Typography variant="body1" sx={{ fontStyle: 'italic', color: colors.greenAccent[400], mt: 0.5 }}>
                                                        Free response questions will be manually graded, which then will be available for review, along with 5-scale AP score estimate.
                                                    </Typography>
                                                ) : (<>
                                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                        {nonMcqLabel} Score: {frq.score}{frq.maxScore > 0 ? ` / ${frq.maxScore}` : ''}
                                                    </Typography>
                                                    {mcq.total > 0 && (
                                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                            Total: {mcq.score + frq.score} / {mcq.total + frq.maxScore}
                                                        </Typography>
                                                    )}
                                                    {testTakerInfo.ap_score != null && (
                                                        <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: colors.greenAccent[400] }}>
                                                            Estimated AP Score: {testTakerInfo.ap_score}
                                                        </Typography>
                                                    )}
                                                </>)}
                                            </>); })()}
                                        </Box>
                                    ) : (
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                            Total Score: {testTakerInfo.score} / {test.test_sections.reduce((acc, section) => acc + section.test_questions.length, 0)}
                                        </Typography>
                                    )
                                )}
                                {sections.map((section, sectionIndex) => (

                                    <Box key={sectionIndex}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                            <Box>
                                                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                    Section {section.section_number}
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                    Number of Questions: {section.test_questions.length}
                                                </Typography>
                                                {testTakerInfo && testTakerInfo.is_completed === true && (test.release_grades || userRole !== "student") && (
                                                    (() => {
                                                        const mcqCount = countSectionMcq(sectionIndex);
                                                        const mcqScore = calculateSectionScore(sectionIndex);
                                                        const frq = computeSectionFrqScore(sectionIndex);
                                                        const hasFrqInSection = frq.maxScore > 0;
                                                        const sectionFrqPending = userRole === 'student' ? isFrqGradingPending() : studentFrqAnswers.some(a => {
                                                            const sectionFrqQuestionIds = section.test_questions.filter(q => q.question_type === 'frq' || q.question_type === 'sa').map(q => q.id);
                                                            return sectionFrqQuestionIds.includes(a.question_id) && (a.grading_status === 'pending' || a.grading_status === 'grading');
                                                        });
                                                        if (hasFrqInSection || sectionFrqPending) {
                                                            return (<>
                                                                {mcqCount > 0 && (
                                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                                        MCQ: {mcqScore} / {mcqCount}
                                                                    </Typography>
                                                                )}
                                                                {sectionFrqPending ? (
                                                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: colors.greenAccent[400], mb: 2 }}>
                                                                        FRQ grading in progress...
                                                                    </Typography>
                                                                ) : (<>
                                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                                                        FRQ: {frq.score} / {frq.maxScore}
                                                                    </Typography>
                                                                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                                        Section Score: {mcqScore + frq.score} / {mcqCount + frq.maxScore}
                                                                    </Typography>
                                                                </>)}
                                                            </>);
                                                        }
                                                        return (
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                                Section Score: {mcqScore} / {mcqCount}
                                                            </Typography>
                                                        );
                                                    })()
                                                )}
                                            </Box>
                                        </Box>
                                        <ul>
                                            {section.test_questions.map((question, qIndex) => {
                                                if (question.question_type === 'frq' || question.question_type === 'sa') {
                                                    return (
                                                        <li key={qIndex}>
                                                            <Typography variant="body1" sx={{ mt: 2, mb: 1, display: "flex", alignItems: "center" }}>
                                                                Q{question.question_number}
                                                                <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                                    ({question.question_type === 'sa' ? 'Short Answer' : 'FRQ'})
                                                                </Typography>
                                                            </Typography>
                                                            {testTakerInfo && testTakerInfo.is_completed === true && (
                                                                renderFrqAnswer(question, test.release_grades || userRole !== "student")
                                                            )}
                                                        </li>
                                                    );
                                                }
                                                const isCorrect = compareAnswers(question.answer, parsedStudentAnswers[sectionIndex]?.[qIndex], question.is_multi_select);
                                                return (
                                                <li key={qIndex}>
                                                    {testTakerInfo && testTakerInfo.is_completed === true && (test.release_grades || userRole !== "student") ? (
                                                        <>
                                                            {isCorrect ? (
                                                                <Typography variant="body1" sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", color: "green" }}>
                                                                    <Typography variant="body1">
                                                                        Q{question.question_number}
                                                                        {question.is_multi_select && (
                                                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                                                (Multi-select)
                                                                            </Typography>
                                                                        )}
                                                                    </Typography>
                                                                    <CheckCircleIcon sx={{ ml: 1 }} />
                                                                </Typography>
                                                            ) : (
                                                                <Typography variant="body1" sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", color: "red" }}>
                                                                    <Typography variant="body1">
                                                                        Q{question.question_number}
                                                                        {question.is_multi_select && (
                                                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                                                (Multi-select)
                                                                            </Typography>
                                                                        )}
                                                                    </Typography>
                                                                    <CancelIcon sx={{ ml: 1 }} />
                                                                </Typography>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Typography variant="body1">
                                                            Q{question.question_number}
                                                            {question.is_multi_select && (
                                                                <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                                    (Multi-select)
                                                                </Typography>
                                                            )}
                                                        </Typography>
                                                    )}
                                                    <ul>
                                                    {question.is_multi_select ? (
                                                        <FormGroup row>
                                                            {question.question_options.map((choice, cIndex) => (
                                                                <FormControlLabel
                                                                    key={cIndex}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={(question.student_pick || '').split(',').includes(choice.option)}
                                                                            disabled={true}
                                                                        />
                                                                    }
                                                                    label={choice.option}
                                                                />
                                                            ))}
                                                        </FormGroup>
                                                    ) : (
                                                        <RadioGroup
                                                            row
                                                            value={question.student_pick || ''}
                                                        >
                                                            {question.question_options.map((choice, cIndex) => (
                                                                <FormControlLabel
                                                                    key={cIndex}
                                                                    value={choice.option}
                                                                    control={<Radio />}
                                                                    label={choice.option}
                                                                    disabled={true}
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                    )}
                                                    </ul>
                                                    {testTakerInfo && testTakerInfo.is_completed === true && (test.release_grades || userRole !== "student") && (
                                                    <>
                                                        {isCorrect ? (
                                                        <>
                                                            <Typography variant="body1" sx={{ mt: 2, mb: 2, color: "green" }}>
                                                                Correct Answer: {question.answer}
                                                            </Typography>
                                                            <Typography variant="body1" sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", color: "green" }}>
                                                                Your Answer: {parsedStudentAnswers[sectionIndex]?.[qIndex] || '(no answer)'}
                                                            </Typography>
                                                        </>
                                                        ) : (
                                                        <>
                                                            <Typography variant="body1" sx={{ mt: 2, mb: 2, color: "green" }}>
                                                                Correct Answer: {question.answer}
                                                            </Typography>
                                                            <Typography variant="body1" sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", color: "red" }}>
                                                                Your Answer: {parsedStudentAnswers[sectionIndex]?.[qIndex] || '(no answer)'}
                                                            </Typography>
                                                        </>
                                                        )}
                                                    </>
                                                    )}
                                                </li>
                                            );})}
                                        </ul>                                
                                    </Box>
                                ))}                              
                            </>
                        ) : (
                        // No Section
                        <>
                            <Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                            Number of Questions: {test.test_questions.length}
                                        </Typography>
                                        {testTakerInfo && testTakerInfo.is_completed === true && (test.release_grades || userRole !== "student") && (
                                            hasFrqQuestions() ? (
                                                <Box sx={{ mb: 2 }}>
                                                    {(() => { const mcq = computeMcqTotal(); const frq = computeFrqTotals(); const pending = isFrqGradingPending(); const hasAnyFrqAnswers = studentFrqAnswers.length > 0; const types = getTestQuestionTypes(); const nonMcqLabel = getNonMcqLabel(); return (<>
                                                        {mcq.total > 0 && (
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                MCQ Score: {mcq.score} / {mcq.total}
                                                            </Typography>
                                                        )}
                                                        {(!hasAnyFrqAnswers || pending) && types.hasFrq ? (
                                                            <Typography variant="body1" sx={{ fontStyle: 'italic', color: colors.greenAccent[400], mt: 0.5 }}>
                                                                Free response questions will be manually graded, which then will be available for review, along with 5-scale AP score estimate.
                                                            </Typography>
                                                        ) : (<>
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                {nonMcqLabel} Score: {frq.score} / {frq.maxScore}
                                                            </Typography>
                                                            {mcq.total > 0 && (
                                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                    Total: {mcq.score + frq.score} / {mcq.total + frq.maxScore}
                                                                </Typography>
                                                            )}
                                                            {testTakerInfo.ap_score != null && (
                                                                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: colors.greenAccent[400] }}>
                                                                    Estimated AP Score: {testTakerInfo.ap_score}
                                                                </Typography>
                                                            )}
                                                                </>)}
                                                    </>); })()}
                                                </Box>
                                            ) : (
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                                    Your Score: {testTakerInfo.score} / {questionsForNoSectionTest.length}
                                                </Typography>
                                            )
                                        )}
                                    </Box>
                                <Box/>

                            </Box>
                                <ul>
                                    {questionsForNoSectionTest.map((question, qIndex) => {
                                        if (question.question_type === 'frq' || question.question_type === 'sa') {
                                            return (
                                                <li key={qIndex}>
                                                    <Typography variant="body1" sx={{ mt: 2, mb: 1, display: "flex", alignItems: "center" }}>
                                                        Q{question.question_number}
                                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                            ({question.question_type === 'sa' ? 'Short Answer' : 'FRQ'})
                                                        </Typography>
                                                    </Typography>
                                                    {testTakerInfo && testTakerInfo.is_completed === true && (
                                                        renderFrqAnswer(question, test.release_grades || userRole !== "student")
                                                    )}
                                                </li>
                                            );
                                        }
                                        const isCorrect = compareAnswers(question.answer, parsedStudentAnswers[qIndex], question.is_multi_select);
                                        return (
                                        <li key={qIndex}>
                                            {testTakerInfo && testTakerInfo.is_completed === true && (test.release_grades || userRole !== "student") ? (
                                                <>
                                                    {isCorrect ? (
                                                        <Typography variant="body1" sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", color: "green" }}>
                                                            <Typography variant="body1">
                                                                Q{question.question_number}
                                                                {question.is_multi_select && (
                                                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                                        (Multi-select)
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                            <CheckCircleIcon sx={{ ml: 1 }} />
                                                        </Typography>
                                                    ) : (
                                                        <Typography variant="body1" sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", color: "red" }}>
                                                            <Typography variant="body1">
                                                                Q{question.question_number}
                                                                {question.is_multi_select && (
                                                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                                        (Multi-select)
                                                                    </Typography>
                                                                )}
                                                            </Typography>
                                                            <CancelIcon sx={{ ml: 1 }} />
                                                        </Typography>
                                                    )}
                                                </>
                                            ) : (
                                                <Typography variant="body1">
                                                    Q{question.question_number}
                                                    {question.is_multi_select && (
                                                        <Typography component="span" variant="caption" sx={{ ml: 1, color: colors.blueAccent[400] }}>
                                                            (Multi-select)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            )}
                                            <ul>
                                                {question.is_multi_select ? (
                                                    <FormGroup row>
                                                        {question.question_options.map((choice, cIndex) => (
                                                            <FormControlLabel
                                                                key={cIndex}
                                                                control={
                                                                    <Checkbox
                                                                        checked={(question.student_pick || '').split(',').includes(choice.option)}
                                                                        disabled={true}
                                                                    />
                                                                }
                                                                label={choice.option}
                                                            />
                                                        ))}
                                                    </FormGroup>
                                                ) : (
                                                    <RadioGroup
                                                        row
                                                        value={question.student_pick || ''}
                                                    >
                                                        {question.question_options.map((choice, cIndex) => (
                                                            <FormControlLabel
                                                                key={cIndex}
                                                                value={choice.option}
                                                                control={<Radio />}
                                                                label={choice.option}
                                                                disabled={true}
                                                            />
                                                        ))}
                                                    </RadioGroup>
                                                )}
                                            </ul>
                                            {testTakerInfo && testTakerInfo.is_completed === true && (test.release_grades || userRole !== "student") && (
                                                <>
                                                {isCorrect ? (
                                                    <>
                                                    <Typography variant="body1" sx={{ mt: 2, mb: 2, color: "green" }}>
                                                        Correct Answer: {question.answer}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", color: "green"}}>
                                                        Your Answer: {parsedStudentAnswers[qIndex] || '(no answer)'}
                                                    </Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                    <Typography variant="body1" sx={{ mt: 2, mb: 2, color: "green" }}>
                                                        Correct Answer: {question.answer}
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", color: "red" }}>
                                                        Your Answer: {parsedStudentAnswers[qIndex] || '(no answer)'}
                                                    </Typography>
                                                    </>
                                                    )
                                                }
                                                </>
                                            )}
                                        </li>
                                    );})}
                                </ul>
                            </Box>                 
                        </>                            
                        ) : (
                            <Typography variant="h5" sx={{ 'fontWeight': 'bold' }}>
                                No Question found in this test
                            </Typography>
                        )}
                        </Box>
                    </Box>
                </>
            }
            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
                vertical="bottom"
                horizontal="left"
            />
        </Box>
    )
}

export default StudentResult;
